import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Calendar, Phone, Search, MessageCircle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const FollowupBadges = ({ followup }) => {
  if (!followup) return null;
  const steps = [
    { key: 'day0_sent', label: 'Day 0', at: followup.day0_sent_at },
    { key: 'day7_sent', label: 'Day 7', at: followup.day7_sent_at },
    { key: 'day21_sent', label: 'Day 21', at: followup.day21_sent_at },
    { key: 'day45_sent', label: 'Day 45', at: followup.day45_sent_at },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="followup-badges">
      <MessageCircle className="w-3.5 h-3.5 text-green-600" />
      <span className="text-xs text-gray-500 mr-1">Follow-up:</span>
      {followup.patient_rebooked ? (
        <Badge className="bg-green-100 text-green-700 text-xs gap-1">
          <CheckCircle2 className="w-3 h-3" /> Rebooked
        </Badge>
      ) : (
        steps.map(s => (
          <Badge
            key={s.key}
            className={`text-xs ${followup[s.key] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
            title={s.at ? `Sent: ${new Date(s.at).toLocaleDateString()}` : 'Pending'}
          >
            {s.label} {followup[s.key] ? '✓' : '—'}
          </Badge>
        ))
      )}
      {followup.status === 'completed' && (
        <Badge className="bg-blue-100 text-blue-700 text-xs ml-1">Sequence Complete</Badge>
      )}
    </div>
  );
};

const NoShowPage = ({ user, onLogout }) => {
  const [noShows, setNoShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNoShow, setSelectedNoShow] = useState(null);
  const [remarks, setRemarks] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [services, setServices] = useState([]);
  const [followups, setFollowups] = useState({});

  useEffect(() => {
    fetchNoShows();
    fetchServices();
    fetchFollowups();
  }, []);

  const fetchNoShows = async () => {
    try {
      const response = await api.get('/no-shows');
      setNoShows(response.data);
    } catch (error) {
      toast.error('Failed to fetch no-shows');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowups = async () => {
    try {
      const response = await api.get('/noshow-followups');
      const map = {};
      response.data.forEach(f => { map[f.no_show_id] = f; });
      setFollowups(map);
    } catch (error) {
      console.error('Failed to fetch followups');
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services');
    }
  };

  const openNoShowModal = (noShow) => {
    setSelectedNoShow(noShow);
    setRemarks(noShow.remarks || '');
  };

  const handleCloseNoShow = async () => {
    if (!remarks.trim()) {
      toast.error('Please add remarks before closing');
      return;
    }

    try {
      await api.put(`/no-shows/${selectedNoShow.id}`, {
        remarks,
        status: 'closed'
      });
      toast.success('No-show closed successfully');
      setSelectedNoShow(null);
      setRemarks('');
      fetchNoShows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close no-show');
    }
  };

  // Filter logic
  const filteredNoShows = noShows.filter(noShow => {
    const matchesSearch = 
      noShow.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      noShow.patient_phone?.includes(searchQuery);
    
    const matchesService = filterService === 'all' || noShow.service_name === filterService;
    const matchesDate = !filterDate || noShow.scheduled_date === filterDate;

    return matchesSearch && matchesService && matchesDate;
  });

  const openNoShows = filteredNoShows.filter(n => n.status === 'open');
  const closedNoShows = filteredNoShows.filter(n => n.status === 'closed');

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">No-Show Management</h1>
          <p className="text-sm text-gray-500">Track and manage patient no-shows</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />

            <Select value={filterService} onValueChange={setFilterService}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map(service => (
                  <SelectItem key={service.id} value={service.name}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={() => {
                setSearchQuery('');
                setFilterDate('');
                setFilterService('all');
              }}
              variant="outline"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Open No-Shows */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-1 w-12 bg-gradient-to-r from-red-400 to-red-600 rounded-full mr-3"></div>
                <h2 className="text-xl font-bold text-gray-900">Open No-Shows</h2>
                <span className="ml-3 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                  {openNoShows.length}
                </span>
              </div>

              {openNoShows.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No open no-shows</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {openNoShows.map(noShow => (
                    <div
                      key={noShow.id}
                      className="bg-white border-2 border-red-200 rounded-xl p-5"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{noShow.patient_name}</h3>
                            <Badge className="bg-purple-100 text-purple-700">
                              {noShow.service_name}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span>{noShow.patient_phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(noShow.scheduled_date)}</span>
                              {noShow.scheduled_time && <span className="ml-1 font-medium">at {noShow.scheduled_time}</span>}
                            </div>
                          </div>
                          <FollowupBadges followup={followups[noShow.id]} />
                        </div>
                        <Button
                          onClick={() => openNoShowModal(noShow)}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid={`close-noshow-btn-${noShow.id}`}
                        >
                          Add Remarks & Close
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Closed No-Shows */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-1 w-12 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full mr-3"></div>
                <h2 className="text-xl font-bold text-gray-900">Closed No-Shows</h2>
                <span className="ml-3 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                  {closedNoShows.length}
                </span>
              </div>

              {closedNoShows.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No closed no-shows</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {closedNoShows.map(noShow => (
                    <div
                      key={noShow.id}
                      className="bg-white border border-gray-200 rounded-xl p-5"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{noShow.patient_name}</h3>
                            <Badge className="bg-purple-100 text-purple-700">
                              {noShow.service_name}
                            </Badge>
                            <Badge className="bg-gray-100 text-gray-700">Closed</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span>{noShow.patient_phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(noShow.scheduled_date)}</span>
                              {noShow.scheduled_time && <span className="ml-1 font-medium">at {noShow.scheduled_time}</span>}
                            </div>
                          </div>
                          {noShow.remarks && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700"><strong>Remarks:</strong> {noShow.remarks}</p>
                            </div>
                          )}
                          {noShow.closed_by_user_name && (
                            <div className="mt-2 text-xs text-gray-500">
                              Closed by {noShow.closed_by_user_name}
                            </div>
                          )}
                          <FollowupBadges followup={followups[noShow.id]} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* No-Show Remarks Modal */}
        {selectedNoShow && (
          <Dialog open={!!selectedNoShow} onOpenChange={() => setSelectedNoShow(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Close No-Show
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">{selectedNoShow.patient_name}</p>
                  <p className="text-sm text-gray-600">{selectedNoShow.service_name}</p>
                  <p className="text-sm text-gray-600">{formatDate(selectedNoShow.scheduled_date)}</p>
                </div>

                <div>
                  <Label>Remarks *</Label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter reason for no-show, follow-up actions, or any notes..."
                    rows={4}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setSelectedNoShow(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCloseNoShow}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                >
                  Close No-Show
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default NoShowPage;
